import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Retry configuration with better defaults
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // Start with 1 second
const MAX_RETRY_DELAY = 5000; // Cap at 5 seconds
const MAX_TIMEOUT = 15000; // 15 seconds timeout
const NETWORK_ERROR_MESSAGE = 'Errore di connessione. Verifica la tua connessione internet e riprova.';

// Function to handle retries with exponential backoff
export async function withRetry<T>(
  operation: () => Promise<T>,
  retries = MAX_RETRIES,
  delay = INITIAL_RETRY_DELAY
): Promise<T> {
  try {
    return await operation();
  } catch (error: any) {
    // Only retry on network errors or timeouts
    if (retries > 0 && (
      error.message?.includes('Failed to fetch') || 
      error.message?.includes('network') ||
      error.message?.includes('timeout') ||
      error.code === '20' || // Network error
      error.code === 'ECONNABORTED' // Connection aborted
    )) {
      console.log(`Retrying operation after ${delay}ms, ${retries} attempts remaining...`);
      const nextDelay = Math.min(delay * 2, MAX_RETRY_DELAY);
      await new Promise(resolve => setTimeout(resolve, delay));
      return withRetry(operation, retries - 1, nextDelay);
    }
    throw error;
  }
}

// Configure Supabase client with better defaults
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    headers: { 'x-application-name': 'attendance-management' }
  },
  db: {
    schema: 'public'
  }
});

// Function to check connection with retry
export async function checkConnection(): Promise<boolean> {
  try {
    const result = await withRetry(async () => {
      const { data, error } = await supabase.from('employees').select('count').limit(1);
      if (error) throw error;
      return data;
    });
    return !!result;
  } catch {
    return false;
  }
}

// Function to handle network errors with better messages
export function handleNetworkError(error: any): string {
  if (error?.message?.includes('Failed to fetch')) {
    return 'Errore di connessione. Verifica la tua connessione internet e riprova tra qualche secondo.';
  }
  if (error?.message?.includes('timeout') || error?.message?.includes('scaduta')) {
    return 'La richiesta ha impiegato troppo tempo. Riprova tra qualche secondo.';
  }
  if (error?.message?.includes('AbortError')) {
    return 'La richiesta è stata interrotta. Riprova.';
  }
  if (error?.code === '20') { // Network error
    return NETWORK_ERROR_MESSAGE;
  }
  if (error?.code === '23505') { // Unique violation
    return 'Esiste già un record con questi dati.';
  }
  if (error?.message?.includes('Codice non valido')) {
    return 'Codice PIN non valido. Verifica il codice e riprova.';
  }
  if (error?.message?.includes('Codice scaduto')) {
    return 'Il codice PIN è scaduto. Contatta l\'amministratore per un nuovo codice.';
  }
  if (error?.message?.includes('Attendere un minuto')) {
    return 'Devi attendere un minuto tra una timbratura e l\'altra.';
  }
  if (error?.message?.includes('non più valido')) {
    return 'Questo codice non è più valido. Contatta l\'amministratore per un nuovo codice.';
  }
  return error?.message || 'Si è verificato un errore imprevisto. Riprova più tardi.';
}